import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import Logo from '../../components/ui/Logo';
import FloatingInput from '../../components/ui/FloatingInput';

// Password strength evaluation helper
function getPasswordStrength(password: string) {
  let score = 0;
  const suggestions = [];
  if (password.length >= 8) score++;
  else suggestions.push('At least 8 characters');
  if ((password.match(/[0-9]/g) || []).length >= 2) score++;
  else suggestions.push('At least 2 numbers');
  if (/[A-Z]/.test(password)) score++;
  else suggestions.push('Add an uppercase letter');
  if (/[^A-Za-z0-9]/.test(password)) score++;
  else suggestions.push('Add a special symbol');
  return { score, suggestions };
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await signUp(formData.email, formData.password, formData.firstName, formData.lastName);
      navigate('/onboarding/genres');
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setError('This email address is already in use. Please use a different email or log in.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/onboarding/genres');
    } catch (err) {
      setError('Failed to sign in with Google. Please try again.');
    } finally {
    setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen flex flex-col items-center bg-[#fdfbf5] font-['Plus Jakarta Sans']">
      <div className="mt-8 mb-4">
        <Logo />
      </div>
      <div className="w-full max-w-[620px] mx-4 sm:mx-auto rounded-[28px] shadow-[0_4px_32px_0_rgba(0,0,0,0.08)] bg-white p-8 sm:p-8 xs:p-4 flex flex-col items-center font-['Plus Jakarta Sans']" style={{ marginBottom: '40px' }}>
        <h2 className="text-3xl font-bold text-gray-900 mb-2 w-full text-left">Create an account</h2>
        <p className="text-gray-700 mb-6 w-full text-left text-base">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-[#ff6600] font-semibold hover:underline"
          >
            Sign in
          </button>
        </p>
        
        <div className="flex w-full gap-3 mb-4">
          <button
            onClick={handleGoogleSignIn}
            type="button"
            className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-full py-2.5 bg-white text-gray-700 font-medium shadow-sm hover:bg-gray-50 transition"
          >
            <img
              className="h-5 w-5"
              src="https://www.svgrepo.com/show/475656/google-color.svg"
              alt="Google logo"
            />
            <span className="text-sm">Sign up with Google</span>
          </button>
        </div>
        <div className="w-full flex justify-center my-4">
          <span className="text-gray-400 text-sm">- OR -</span>
        </div>

        <form className="w-full flex flex-col items-center" onSubmit={handleSubmit}>
          {error && <div className="text-red-600 text-sm mb-8 w-full max-w-[570px]">{error}</div>}
          
          <div className="w-full max-w-[570px] grid grid-cols-2 gap-4 mb-8">
            <FloatingInput
                  id="firstName"
                  name="firstName"
                  type="text"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First name"
              autoComplete="given-name"
              noBottomMargin={true}
            />
            <FloatingInput
                  id="lastName"
                  name="lastName"
                  type="text"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last name"
              autoComplete="family-name"
              noBottomMargin={true}
            />
            </div>

          <FloatingInput
                  id="email"
                name="email"
                type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
                autoComplete="email"
          />
          
          <FloatingInput
                id="password"
                name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="Password"
                autoComplete="new-password"
            showPasswordToggle={true}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
          />
          {formData.password.length > 0 && (
            <div className="w-full max-w-[570px] mb-4" style={{ marginTop: '-16px' }}>
              <div className="flex items-center gap-2 mt-0 mb-0">
                {[0,1,2,3].map(i => (
                  <div key={i} className={`flex-1 h-2 rounded-full ${
                    passwordStrength.score > i ?
                      (passwordStrength.score <= 1 ? 'bg-red-400' : passwordStrength.score === 2 ? 'bg-yellow-400' : 'bg-green-400')
                      : 'bg-gray-200'
                  }`} />
                ))}
              </div>
              <div className="text-xs text-gray-500">
                {passwordStrength.suggestions.length === 0
                  ? <span className="text-green-600">Strong password!</span>
                  : <>
                      {passwordStrength.suggestions.map((s, idx) => (
                        <span key={s} className="mr-2">{s}{idx < passwordStrength.suggestions.length-1 ? ',' : ''}</span>
                      ))}
                    </>
                }
              </div>
            </div>
          )}
          
          <FloatingInput
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm password"
            autoComplete="new-password"
            showPasswordToggle={true}
            showPassword={showConfirmPassword}
            setShowPassword={setShowConfirmPassword}
          />

              <button
                type="submit"
                disabled={loading}
            className="w-full max-w-[570px] py-3 rounded-full bg-[#333333] text-white font-semibold text-base shadow-sm hover:bg-gray-800 transition disabled:opacity-60 font-['Plus Jakarta Sans']"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', backgroundColor: '#333333' }}
              >
            {loading ? 'Creating account...' : 'Create account'}
              </button>
          </form>
      </div>
    </div>
  );
};

export default Signup; 